import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { HairProductKey } from "~/api/models/HairProductKey";
import { SkinProductKey } from "~/api/models/SkinProductKey";
import { useCalculatePrice } from "~/api/salesForce/useCalculatePrice";
import { useFetchOpportunity } from "~/api/salesForce/useFetchOpportunity";
import Copy from "~/components/atom/Copy";
import Button from "~/components/atoms/Button/Button";
import Headline from "~/components/atoms/Headline/Headline";
import SpinnerIcon from "~/components/atoms/SpinnerIcon/SpinnerIcon";
import Subtitle from "~/components/atoms/Subtitle/Subtitle";
import EditableDiscount from "~/components/molecules/EditableDiscount/EditableDiscount";
import EditablePaymentOption from "~/components/molecules/EditablePaymentOption/EditablePaymentOption";
import EditableSpecialConditions from "~/components/molecules/EditableSpecialConditions/EditableSpecialConditions";
import HeyLightContractModal from "~/components/molecules/HeyLightContractModal/HeyLightContractModal";
import PricingCardItem from "~/components/molecules/PricingCardItem/PricingCardItem";
import PricingDropDown from "~/components/molecules/PricingDropdown/PricingDropdown";
import useRequestCreateContract from "~/hooks/useRequestCreateContract";
import { useCheckoutStore } from "~/stores/checkout/useCheckoutStore";
import { cn } from "~/utils";
import ContractLoader from "../../ContractLoader/ContractLoader";

const PricingCard = () => {
  const posthog = usePostHog();
  const intl = useIntl();
  const { opportunityId } = useParams();
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [
    selectedProducts,
    selectedBundles,
    hasSkinCare,
    paymentMethod,
    discountCode,
    referralCode,
    specialConditionsValues,
    otherSpecialConditions,
    heyLightOrderId,
    selectedPaymentOption,
    individualDiscountAmount,
    individualDiscountPercent,
  ] = useCheckoutStore((state) => [
    state.selectedProducts,
    state.selectedBundles,
    state.hasSkinCare,
    state.paymentMethod,
    state.discountCode,
    state.referralCode,
    state.specialConditionsValues,
    state.otherSpecialConditions,
    state.heyLightOrderId,
    state.selectedPaymentOption,
    state.individualDiscountAmount,
    state.individualDiscountPercent,
  ]);

  const { data: opportunity } = useFetchOpportunity({
    id: opportunityId as string,
    hasRefetch: true,
  });
  const { data: pricing, isLoading } = useCalculatePrice({
    id: opportunityId as string,
    body: {
      products: selectedProducts,
      selectedBundles: selectedBundles,
      hasSkinCare: hasSkinCare,
      paymentMethod: paymentMethod ?? null,
      discountCode: discountCode,
      referralCode: referralCode,
      specialConditions: specialConditionsValues ?? [],
      otherSpecialConditions,
      individualDiscountAmount: individualDiscountAmount,
      individualDiscountPercent: individualDiscountPercent,
    },
    disabled: !opportunity?.isValidForSales,
  });

  const hasFreeProducts = useMemo(() => {
    return (
      selectedProducts.some(
        (product) => product.productCardKey === HairProductKey.freePrp
      ) ||
      selectedProducts.some(
        (product) => product.productCardKey === HairProductKey.freeMesotherapy
      ) ||
      selectedProducts.some(
        (product) => product.productCardKey === SkinProductKey.freeBotox
      )
    );
  }, [selectedProducts]);

  const hasProducts = useMemo(
    () => selectedProducts.length > 0 || selectedBundles.length > 0,
    [selectedProducts.length, selectedBundles.length]
  );

  const shouldUsePricingData = useMemo(() => {
    if (!opportunity?.lastModifiedDateTime || !pricing?.lastModifiedDateTime) {
      return false;
    }
    return pricing.lastModifiedDateTime > opportunity.lastModifiedDateTime;
  }, [opportunity?.lastModifiedDateTime, pricing?.lastModifiedDateTime]);

  const totalAmount = useMemo(() => {
    const amount = shouldUsePricingData
      ? pricing?.totalAmount
      : opportunity?.amount;
    return amount ? Number(amount.toFixed(2)) : undefined;
  }, [shouldUsePricingData, pricing?.totalAmount, opportunity?.amount]);

  const discountPercent = useMemo(() => {
    return shouldUsePricingData
      ? pricing?.discountPercent
      : opportunity?.discountPercent ?? pricing?.discountPercent;
  }, [
    shouldUsePricingData,
    opportunity?.discountPercent,
    pricing?.discountPercent,
  ]);

  const discountedAmount = useMemo(() => {
    const amount = shouldUsePricingData
      ? pricing?.discountedAmount
      : opportunity?.discountedAmount ?? pricing?.discountedAmount;
    return amount ? Number(amount.toFixed(2)) : undefined;
  }, [
    shouldUsePricingData,
    opportunity?.discountedAmount,
    pricing?.discountedAmount,
  ]);

  const showDiscountedAmount = useMemo(() => {
    return discountedAmount !== undefined && discountedAmount !== totalAmount;
  }, [discountedAmount, totalAmount]);

  const discountReason = useMemo(() => {
    return shouldUsePricingData
      ? pricing?.discountName
      : opportunity?.discountReason ?? pricing?.discountName;
  }, [
    shouldUsePricingData,
    opportunity?.discountReason,
    pricing?.discountName,
  ]);

  const showDiscountReason = useMemo(() => {
    return discountReason != undefined && showDiscountedAmount;
  }, [discountReason, showDiscountedAmount]);

  const discountTotal = useMemo(() => {
    return shouldUsePricingData
      ? pricing?.totalDiscount
      : opportunity?.discountTotal ?? pricing?.totalDiscount;
  }, [
    shouldUsePricingData,
    opportunity?.discountTotal,
    pricing?.totalDiscount,
  ]);

  const requestContract = () => {
    if (paymentMethod === "HeyLight") {
      setIsContractModalOpen(true);
    } else {
      posthog.capture("request_contract", {
        opportunityId: opportunityId,
        selectedProducts: selectedProducts,
        selectedBundles: selectedBundles,
        paymentMethod: paymentMethod,
        heyLightOrderId: heyLightOrderId,
      });
      onCreateContract();
    }
  };

  const { onCreateContract, requestingContract, composingContract } =
    useRequestCreateContract({
      opportunityId: opportunityId as string,
      selectedProducts,
      selectedBundles,
      paymentMethod,
      heyLightOrderId,
    });

  const isCreateContractButtonDisabled = useMemo(() => {
    return !selectedPaymentOption;
  }, [selectedPaymentOption]);

  return (
    <div className="flex flex-col gap-6 bg-beige-100 rounded-2xl p-6 justify-between flex-1">
      <ContractLoader showLoader={composingContract || requestingContract} />
      <HeyLightContractModal
        open={isContractModalOpen}
        setOpen={setIsContractModalOpen}
        onSuccess={onCreateContract}
        discountedAmount={discountedAmount}
      />
      <div className="flex flex-col gap-4">
        <div className="flex gap-2 justify-between items-center">
          <Headline level={4}>
            <Copy id="pricing.card.total" />
          </Headline>
          {opportunity?.isValidForSales && <PricingDropDown />}
        </div>
        <div className="flex flex-col gap-6">
          {isLoading && hasProducts && opportunity?.isValidForSales ? (
            <SpinnerIcon />
          ) : (
            <div className="flex gap-8 items-center justify-between">
              <Headline
                level={5}
                className={cn({
                  "text-grey-400 line-through": showDiscountedAmount,
                })}
              >
                {intl.formatMessage(
                  { id: "pricing.amount" },
                  { amount: hasProducts ? totalAmount : 0 }
                )}
              </Headline>
              <Headline
                level={5}
                className={cn(
                  "text-grey-700 opacity-0 transition-all duration-300",
                  {
                    "opacity-100": showDiscountedAmount,
                  }
                )}
              >
                {intl.formatMessage(
                  { id: "pricing.amount" },
                  { amount: discountedAmount }
                )}
              </Headline>
            </div>
          )}
          {hasProducts && (
            <div className="flex flex-col gap-3">
              <PricingCardItem
                title={intl.formatMessage({
                  id: "pricing.card.paymentMethod",
                })}
              >
                <EditablePaymentOption
                  editable={opportunity?.isValidForSales}
                />
              </PricingCardItem>
              <PricingCardItem
                title={intl.formatMessage({
                  id: "pricing.card.discount",
                })}
              >
                <EditableDiscount
                  showDiscountedAmount={showDiscountedAmount}
                  discountPercent={discountPercent}
                  discountTotal={discountTotal}
                  editable={opportunity?.isValidForSales}
                  hasFreeProducts={hasFreeProducts}
                />
              </PricingCardItem>
              {showDiscountReason && (
                <PricingCardItem
                  title={intl.formatMessage({
                    id: "pricing.card.discount.reason",
                  })}
                >
                  <Subtitle level={3}>{discountReason}</Subtitle>
                </PricingCardItem>
              )}
              <PricingCardItem
                title={intl.formatMessage({
                  id: "pricing.specialConditions.title",
                })}
              >
                <EditableSpecialConditions
                  editable={opportunity?.isValidForSales}
                />
              </PricingCardItem>
            </div>
          )}
        </div>
      </div>
      {opportunity?.isValidForSales && (
        <Button
          text={intl.formatMessage({ id: "pricing.card.cta" })}
          className="w-full max-w-full"
          onClick={requestContract}
          disabled={isCreateContractButtonDisabled}
        />
      )}
    </div>
  );
};

export default PricingCard;
